import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import type { Meilisearch_ShippingCountry } from '@/lib/api/meilisearch-types';
import { fetchShippingCountries } from '@/lib/api/shipping-countries';
import type { PossibleLocales } from '@/utils/AppConfig';

type ShippingCountryWithLocal = Meilisearch_ShippingCountry & {
  nom_local?: string;
};

interface CountryPickerProps {
  selectedCountry: ShippingCountryWithLocal | null;
  onChange: (country: ShippingCountryWithLocal | null) => void;
  placeholder?: string;
}

const CountryPicker: React.FC<CountryPickerProps> = ({
  selectedCountry,
  onChange,
  placeholder = 'Choisir un pays',
}) => {
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<ShippingCountryWithLocal[]>([]);
  const locale = useLocale() as PossibleLocales;

  const fetchCountries = useCallback(
    async (searchedCountry: string) => {
      setLoading(true);
      const res = await fetchShippingCountries(searchedCountry, { limit: 500 });
      const mapped = res.hits.map((elt) => ({
        ...elt,
        nom_fr: elt.fr.nom,
        nom_en: elt.en.nom,
      })) as ShippingCountryWithLocal[];
      mapped.sort((a, b) => a[locale].nom.localeCompare(b[locale].nom, locale));
      setCountries(mapped);
      setLoading(false);
    },
    [locale],
  );
  const t = useTranslations('Checkout.SectionAdresse');
  return (
    <div>
      <Combobox immediate value={selectedCountry} onChange={onChange}>
        <div className="relative mt-1">
          <div className="relative mt-1">
            <ComboboxInput
              // @ts-expect-error
              autocomplete="off"
              className="w-full rounded-md border-gray-300"
              onChange={(event) => fetchCountries(event.target.value)}
              onFocus={() => {
                if (countries.length === 0) fetchCountries('');
              }}
              displayValue={(country: ShippingCountryWithLocal) =>
                country?.[locale]?.nom || ''
              }
              placeholder={placeholder}
            />
            {selectedCountry && (
              <button
                type="button"
                className="absolute inset-y-0 right-0 z-10 flex items-center pr-3"
                onClick={() => onChange(null)}
              >
                <XMarkIcon className="size-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {loading && (
            <div className="absolute right-0 top-0 mr-2 mt-2">
              <div className="loading loading-spinner loading-xs" />
            </div>
          )}
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            {countries.length === 0 ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                {t(
                  'AjoutEditionAdresse.Formulaire.Champs.pays.aucun_pays_disponible',
                )}
              </div>
            ) : (
              countries.map((country) => (
                <ComboboxOption
                  key={country.id}
                  className={({ focus, disabled }) =>
                    twMerge(
                      `relative cursor-pointer select-none py-2 pl-3 pr-4 text-gray-900`,
                      focus && !disabled ? 'bg-secondary-400 text-white' : '',
                      focus && disabled ? 'bg-gray-200 ' : '',
                      disabled
                        ? 'text-gray-500 bg-gray-100 cursor-not-allowed'
                        : '',
                    )
                  }
                  // disabled={!country.actif}
                  value={country}
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {country[locale].nom}
                        {country.nom_local && (
                          <span className="ml-1 opacity-60">
                            ({country.nom_local})
                          </span>
                        )}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? 'text-white' : 'text-green-900'
                          }`}
                        >
                          <CheckCircleIcon className="size-5" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
};

export default CountryPicker;
